import React, { Component } from 'react';
import { withGoogleMap, GoogleMap, Marker, InfoWindow, Polyline } from 'react-google-maps';

const PRIMARY = '#6f42c1';
const SELECTED = '#16a34a';
const DROPOFF = '#dc3545';

const pointKey = p => p.lat.toFixed(5) + ',' + p.lng.toFixed(5);

// The club's run route in Seq order (the card order: 1, 2, 3 …), using the stored coordinates as-is:
// every pickup in sequence, then the drop-off(s) in sequence. Identical points are visited once, so a
// shared airport drop-off (departures) is a single final stop and a shared airport pickup (arrivals)
// is a single first stop followed by each drop-off in sequence.
// cardStop[i] = index in `stops` of the point that card i adds to the run (its pickup, or its
// drop-off when every passenger shares one pickup), used for the "Leg" pill above each card.
export function buildRunStops(reservations) {
    const stops = [];
    const indexByKey = new Map();
    const add = (point, kind) => {
        const key = kind + ':' + pointKey(point);
        if (!indexByKey.has(key)) {
            indexByKey.set(key, stops.length);
            stops.push(point);
        }
        return indexByKey.get(key);
    };
    const pickupStop = reservations.map(r => (r.pickup ? add(r.pickup, 'P') : null));
    const dropoffStop = reservations.map(r => (r.dropoff ? add(r.dropoff, 'D') : null));
    const sharedPickup = new Set(reservations.filter(r => r.pickup).map(r => pointKey(r.pickup))).size === 1;
    return { stops, cardStop: sharedPickup ? dropoffStop : pickupStop };
}

// Passengers at the same point (very common: an airport) share one marker, so their names are
// listed together instead of stacking identical markers/info windows on top of each other.
// `kind` is 'pickup' or 'dropoff'; the group key is prefixed with it so the two never clash.
function groupPoints(reservations, kind) {
    const addressField = kind === 'pickup' ? 'puStreet' : 'doStreet';
    const groups = new Map();
    reservations.forEach((r, index) => {
        const point = r[kind];
        if (!point) {
            return;
        }
        const key = kind + ':' + pointKey(point);
        if (!groups.has(key)) {
            groups.set(key, { key, kind, position: point, address: r[addressField], stops: [] });
        }
        groups.get(key).stops.push({ order: index + 1, reservation: r });
    });
    return Array.from(groups.values());
}

function circleIcon(color, scale) {
    return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
    };
}

function pinIcon(color, scale) {
    return {
        path: 'M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 1.5,
        scale,
        anchor: new window.google.maps.Point(12, 22),
    };
}

// Large translucent ring drawn behind the selected passenger's markers.
function haloIcon() {
    return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 26,
        fillColor: SELECTED,
        fillOpacity: 0.18,
        strokeColor: SELECTED,
        strokeOpacity: 0.6,
        strokeWeight: 2,
    };
}

// How long the selected passenger's marker bounces when picked (about two bounces).
const HIGHLIGHT_BOUNCE_MS = 1400;

// Arrow heads repeated along a line, so the travel direction (Seq 1 → 2 → … → drop-off) is visible.
function directionArrows(color) {
    return [{
        icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.6, strokeColor: color, strokeWeight: 2, fillColor: color, fillOpacity: 1 },
        offset: '40px',
        repeat: '110px',
    }];
}

// Dashed straight line (no road route available): a dash symbol repeated along an invisible stroke.
function dashedLine(color) {
    return [
        { icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: color, strokeWeight: 3, scale: 3 }, offset: '0', repeat: '14px' },
        ...directionArrows(color),
    ];
}

// Info window on a pickup/drop-off marker: a "Pickup" / "Drop-off" heading and the passenger name(s)
// only, to keep it compact on the map. The address is still the marker's hover tooltip.
function PointInfo({ group, selectedRideId, onSelect }) {
    const isPickup = group.kind === 'pickup';
    return (
        <div className="cr-map-info cr-map-info-compact">
            <span className="cr-map-info-label">
                <i className={isPickup ? 'fas fa-circle cr-route-pickup' : 'fas fa-map-marker-alt cr-route-dropoff'}></i> {isPickup ? 'Pickup' : 'Drop-off'}
            </span>
            {group.stops.map(s => (
                <button
                    type="button"
                    key={s.reservation.rideId}
                    className={`cr-map-info-pax${s.reservation.rideId === selectedRideId ? ' cr-map-info-pax-selected' : ''}`}
                    title="Highlight this passenger on the map"
                    onClick={() => onSelect(s.reservation.rideId)}
                >
                    <span className="cr-map-info-order">{s.order}</span> {s.reservation.paxName}
                </button>
            ))}
        </div>
    );
}

// Rendered only once the Google Maps script has loaded (ClubReservationsComponent gates on mapsReady),
// so window.google.maps is guaranteed to exist here.
const GoogleMapView = withGoogleMap(props => (
    <GoogleMap
        ref={props.onMapMounted}
        defaultCenter={props.defaultCenter}
        defaultZoom={12}
        defaultOptions={{
            mapTypeControl: true,
            streetViewControl: true,
            zoomControl: true,
            fullscreenControl: false,
            gestureHandling: 'greedy',
        }}
    >
        {/* Run route in Seq order, one segment per consecutive pair of stops: pickup 1 → pickup 2 → … →
            drop-off, each with direction arrows. A segment Google has no road route for is drawn as a
            dashed straight line, so the order is always visible. Until the routes arrive, the whole run
            is shown as dashed straight segments. */}
        {props.runSegments.length > 0
            ? props.runSegments.map((s, k) => (
                <Polyline
                    key={'seg:' + k + ':' + pointKey(s.from) + '>' + pointKey(s.to)}
                    path={s.path || [s.from, s.to]}
                    options={s.path
                        ? { strokeColor: PRIMARY, strokeWeight: 5, strokeOpacity: 0.85, zIndex: 1, icons: directionArrows(PRIMARY) }
                        : { strokeOpacity: 0, zIndex: 1, icons: dashedLine(PRIMARY) }}
                />
            ))
            : props.runStops.length >= 2 &&
                <Polyline path={props.runStops} options={{ strokeOpacity: 0, zIndex: 1, icons: dashedLine(PRIMARY) }} />}

        {/* Selected passenger: soft green halo behind their pickup (and drop-off) so the location stands out. */}
        {props.highlightPoints.map(p => (
            <Marker key={'halo:' + pointKey(p)} position={p} icon={haloIcon()} clickable={false} zIndex={0} />
        ))}

        {props.groups.map(group => {
            const isSelectedGroup = group.stops.some(s => s.reservation.rideId === props.selectedRideId);
            const isPickup = group.kind === 'pickup';
            return (
                <Marker
                    key={group.key}
                    position={group.position}
                    icon={isPickup
                        ? circleIcon(isSelectedGroup ? SELECTED : PRIMARY, isSelectedGroup ? 15 : 13)
                        : pinIcon(DROPOFF, isSelectedGroup ? 1.9 : 1.6)}
                    label={isPickup ? { text: group.stops.map(s => s.order).join(','), color: '#fff', fontWeight: 'bold', fontSize: '11px' } : undefined}
                    title={group.stops.map(s => s.reservation.paxName).join(', ') + ' — ' + (group.address || '')}
                    zIndex={isSelectedGroup ? 4 : (isPickup ? 2 : 3)}
                    animation={isSelectedGroup && props.isBouncing ? window.google.maps.Animation.BOUNCE : null}
                    onClick={() => props.onMarkerClick(group)}
                >
                    {!props.closedInfoKeys.has(group.key) &&
                        <InfoWindow options={{ disableAutoPan: true }} onCloseClick={() => props.onInfoClose(group.key)}>
                            <PointInfo group={group} selectedRideId={props.selectedRideId} onSelect={props.onSelect} />
                        </InfoWindow>}
                </Marker>
            );
        })}
    </GoogleMap>
));

// Club Reservations map: a marker per pickup point (pickupLatitude/Longitude, puStreet) and per drop-off
// point (dropoffLatitude/Longitude, doStreet), and the run route in Seq order (runSegments, calculated
// once by ClubReservationsComponent so the Leg pills and Total match the line). The run route is always
// shown; selecting a passenger (card or marker) only highlights their location on it.
export class ClubReservationsMap extends Component {
    constructor(props) {
        super(props);
        this.state = { closedInfoKeys: new Set(), isBouncing: false };
        this.mapRef = null;
        this.bounceTimer = null;
        this.handleMapMounted = this.handleMapMounted.bind(this);
        this.handleMarkerClick = this.handleMarkerClick.bind(this);
        this.handleInfoClose = this.handleInfoClose.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.selectedRideId !== this.props.selectedRideId) {
            this.reopenInfoForSelected();
            this.focusSelected();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.bounceTimer);
    }

    // Highlights the selected passenger: pan to their pickup (drop-off if they have no pickup) without
    // changing the zoom, so the run route stays in view, and bounce their marker briefly.
    // Clearing the selection fits the whole run route again.
    focusSelected() {
        clearTimeout(this.bounceTimer);
        const selected = this.getSelected();
        if (!selected) {
            this.setState({ isBouncing: false });
            this.fitToView();
            return;
        }
        const point = selected.pickup || selected.dropoff;
        if (point && this.mapRef) {
            this.mapRef.panTo(point);
        }
        this.setState({ isBouncing: true });
        this.bounceTimer = setTimeout(() => this.setState({ isBouncing: false }), HIGHLIGHT_BOUNCE_MS);
    }

    // Info-window keys of a passenger's pickup and drop-off markers.
    infoKeysFor(r) {
        return [
            r.pickup ? 'pickup:' + pointKey(r.pickup) : null,
            r.dropoff ? 'dropoff:' + pointKey(r.dropoff) : null,
        ].filter(Boolean);
    }

    // True when the given passenger has a pickup or drop-off info window closed with ✕.
    hasClosedInfoFor(rideId) {
        const r = this.props.reservations.find(x => x.rideId === rideId);
        return !!r && this.infoKeysFor(r).some(key => this.state.closedInfoKeys.has(key));
    }

    // Info windows the dispatcher closed (✕) come back when that passenger is selected again,
    // from the card or the map: their pickup window and their drop-off window both reopen.
    reopenInfoForSelected() {
        const selected = this.getSelected();
        if (!selected) {
            return;
        }
        this.setState(prev => {
            const closedInfoKeys = new Set(prev.closedInfoKeys);
            this.infoKeysFor(selected).forEach(key => closedInfoKeys.delete(key));
            return { closedInfoKeys };
        });
    }

    handleMapMounted(map) {
        this.mapRef = map;
        this.fitToView();
    }

    handleMarkerClick(group) {
        // A shared point lists its passengers in the info window; a single passenger selects directly.
        this.setState(prev => {
            const closedInfoKeys = new Set(prev.closedInfoKeys);
            closedInfoKeys.delete(group.key);
            return { closedInfoKeys };
        });
        if (group.stops.length === 1) {
            this.props.onSelect(group.stops[0].reservation.rideId);
        }
    }

    handleInfoClose(key) {
        this.setState(prev => ({ closedInfoKeys: new Set(prev.closedInfoKeys).add(key) }));
    }

    getSelected() {
        return this.props.reservations.find(r => r.rideId === this.props.selectedRideId) || null;
    }

    // Fits the whole run route (every stop). Used when the map is first shown and when the selection is cleared.
    fitToView() {
        if (!this.mapRef) {
            return;
        }
        const points = buildRunStops(this.props.reservations).stops;
        if (points.length === 0) {
            return;
        }
        if (points.length === 1) {
            this.mapRef.panTo(points[0]);
            return;
        }
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach(p => bounds.extend(p));
        this.mapRef.fitBounds(bounds, 60);
    }

    render() {
        const { reservations, selectedRideId, onSelect, runSegments } = this.props;
        const selected = this.getSelected();
        const groups = [...groupPoints(reservations, 'pickup'), ...groupPoints(reservations, 'dropoff')];
        const firstPickup = reservations.map(r => r.pickup).find(Boolean);
        const missingPickup = reservations.filter(r => !r.pickup);
        const missingDropoff = reservations.filter(r => !r.dropoff);
        const names = rows => rows.map(r => r.paxName).join(', ');

        return (
            <>
                {(missingPickup.length > 0 || missingDropoff.length > 0) &&
                    <div className="cr-map-note">
                        {missingPickup.length > 0 && <div><i className="fas fa-info-circle"></i> No pickup location for: <b>{names(missingPickup)}</b>.</div>}
                        {missingDropoff.length > 0 && <div><i className="fas fa-info-circle"></i> No drop-off location for: <b>{names(missingDropoff)}</b>.</div>}
                    </div>}
                <div className="cr-map-legend">
                    <span><span className="cr-legend-line cr-legend-run"></span> Run route (Seq 1 &rarr; {reservations.length} &rarr; drop-off)</span>
                    {selected && <span><span className="cr-legend-dot"></span> {selected.paxName}</span>}
                </div>
                <GoogleMapView
                    containerElement={<div className="cr-map" />}
                    mapElement={<div style={{ height: '100%' }} />}
                    defaultCenter={firstPickup || { lat: 25.798939, lng: -80.291409 }}
                    onMapMounted={this.handleMapMounted}
                    groups={groups}
                    selectedRideId={selectedRideId}
                    runSegments={runSegments || []}
                    runStops={buildRunStops(reservations).stops}
                    highlightPoints={selected ? [selected.pickup, selected.dropoff].filter(Boolean) : []}
                    isBouncing={this.state.isBouncing}
                    closedInfoKeys={this.state.closedInfoKeys}
                    onMarkerClick={this.handleMarkerClick}
                    onInfoClose={this.handleInfoClose}
                    onSelect={onSelect}
                />
            </>
        );
    }
}
